import { useState } from "react";
import "./GroupView.scss";
import EditGroupModal from "./EditGroupModal";
import CreateGroupModal from "./CreateGroupModal";
import { useGetGroups, createGroup, updateGroup } from "../../../../../hooks/useGetGroups";
import GroupUtils from "../../../../../utils/groupUtils";
import { toast } from "react-toastify";
import { trackEvent } from "../../../../../mixpanel";

type Group = {
  id: string;
  name: string;
  students: Array<{ id: string; name: string }>;
  assignedStories: Array<{ id: string }>;
  updatedAt: string;
  isDefault?: boolean;
};

interface GroupViewProps {
  schoolHash: string;
  grade: string;
}

const GroupView: React.FC<GroupViewProps> = ({ schoolHash, grade }) => {
  const { data, loading, error, refetch } = useGetGroups({
    schoolHash,
    grade,
    enabled: true
  });

  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);
  const [editingGroup, setEditingGroup] = useState<Group | null>(null);
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [isCreatingGroup, setIsCreatingGroup] = useState<boolean>(false);
  const [isUpdatingGroup, setIsUpdatingGroup] = useState<boolean>(false);

  // Get all groups from API data
  const defaultGroup = data?.defaultGroup;
  const customGroups = data?.customGroups || [];
  
  // All available students in the class
  const allStudents = data ? GroupUtils.getAllStudents(data).map(s => s.name) : [];
  // Get default group students for create modal
  const defaultGroupStudents = data ? data.defaultGroup.students : [];
  
  // Get students already in custom groups
  const studentsInCustomGroups = data ? 
    data.customGroups.flatMap(group => group.students.map(student => student.name)) : [];
  
  // Get students who are not in any custom group (unassigned students)
  const unassignedStudents = defaultGroupStudents.filter(
    student => !studentsInCustomGroups.includes(student.name)
  );
  
  // Get count of custom groups
  const customGroupsCount = data ? data.customGroups.length : 0;

  // Existing group names (custom + default) to prevent duplicate names on create
  const existingGroupNames = data
    ? [...data.customGroups.map(g => g.name), data.defaultGroup.name]
    : [];

  const toggleGroup = (groupId: string) => {
    const isExpanding = !expandedGroups.includes(groupId);
    if (isExpanding) {
      const group = [...customGroups, defaultGroup].find(g => g?.id === groupId);
      trackEvent("GROUP_SECTION_EXPANDED", {
        section: "Group Students List",
        groupId,
      });
    }
    setExpandedGroups((prev) =>
      prev.includes(groupId)
        ? prev.filter((id) => id !== groupId)
        : [...prev, groupId]
    );
  };

  const handleCreateGroup = () => {
    // Check if max groups (3) already exist
    if (customGroupsCount >= 3) {
      toast.error("Max. of 3 groups can be created");
      return;
    }
    setShowCreateModal(true);
  };

  const handleEditGroup = (groupId: string) => {
    if (data && !GroupUtils.canEditGroup(groupId, data)) {
      console.log('Cannot edit default group');
      return;
    }
    const group = customGroups.find((g) => g.id === groupId);
    if (group) {
      setEditingGroup(group);
    }
  };

  const handleSaveGroup = async (selectedStudentNames: string[]) => {
    if (!editingGroup || !data || !schoolHash || !grade || isUpdatingGroup) return;

    try {
      setIsUpdatingGroup(true);
      
      // Convert student names to student objects
      const selectedStudents = defaultGroupStudents.filter(student =>
        selectedStudentNames.includes(student.name)
      );

      // Make API call to update group
      await updateGroup(
        schoolHash,
        data.classId,
        editingGroup.name,
        selectedStudents
      );

      // Close modal and refetch data after success
      setEditingGroup(null);
      refetch();
    } catch (error) {
      console.error('Failed to update group:', error);
      // TODO: Show error message to user
    } finally {
      setIsUpdatingGroup(false);
    }
  };

  const handleDeleteGroup = (groupId: string) => {
    if (data && !GroupUtils.canDeleteGroup(groupId, data)) {
      console.log('Cannot delete default group');
      return;
    }
    // TODO: Implement API call to delete group
    console.log('Delete group:', groupId);
    // After successful API call, refetch the data
    refetch();
  };

  const handleCreateNewGroup = async (groupName: string, selectedStudentNames: string[]) => {
    if (!data || !schoolHash || !grade || isCreatingGroup) return;

    try {
      setIsCreatingGroup(true);
      
      // Convert student names to student objects
      const selectedStudents = defaultGroupStudents.filter(student =>
        selectedStudentNames.includes(student.name)
      );

      // Make API call to create group
      await createGroup(
        schoolHash,
        data.classId,
        groupName,
        selectedStudents
      );

      // Track group creation
      trackEvent("GROUP_CREATED", {
        groupName,
        studentsCount: selectedStudents.length,
        grade,
        totalCustomGroups: customGroupsCount + 1,
      });

      // Close modal and refetch data after success
      setShowCreateModal(false);
      refetch();
    } catch (error) {
      console.error('Failed to create group:', error);
      // TODO: Show error message to user
    } finally {
      setIsCreatingGroup(false);
    }
  };

  return (
    <div className="group-view">
      {loading && (
        <div className="loading-state">
          <p>Loading groups...</p>
        </div>
      )}

      {error && (
        <div className="error-state">
          <p>Error: {error}</p>
          <button onClick={refetch} className="retry-btn">Retry</button>
        </div>
      )}

      {!loading && !error && data && (
        <>
          {/* Whole Class Group - Always at top */}
          {defaultGroup && (
            <div className="whole-class-section">
              <div className="my-classgroup-header">
                <h2 className="classsection-title">My Classroom</h2>
              </div>
              <div className="group-item default-group">
                <div className="group-row">
                  <div className="group-left">
                    <h3 className="group-name">{defaultGroup.name}</h3>
                    <button
                      className="student-list-toggle"
                      onClick={() => toggleGroup(defaultGroup.id)}
                    >
                      <span>{defaultGroup.students.length} students</span>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path
                          d="M4 6L8 10L12 6"
                          stroke="#9747FF"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          style={{
                            transform: expandedGroups.includes(defaultGroup.id)
                              ? "rotate(180deg)"
                              : "rotate(0deg)",
                            transformOrigin: "center",
                            transition: "transform 0.3s ease",
                          }}
                        />
                      </svg>
                    </button>
                  </div>
                </div>
                
                {expandedGroups.includes(defaultGroup.id) && (
                  <div className="student-list-bottom">
                    {defaultGroup.students.length > 0 ? (
                      <div className="student-columns">
                        {Array.from({ length: Math.ceil(defaultGroup.students.length / 5) }, (_, columnIndex) => (
                          <div key={columnIndex} className="student-column">
                            {defaultGroup.students
                              .slice(columnIndex * 5, (columnIndex + 1) * 5)
                              .map((student, index) => (
                                <span key={student.id || index} className="student-name">
                                  {student.name}
                                </span>
                              ))
                            }
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span className="no-students">No students in this class</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* My Groups Section */}
          <div className="my-groups-section">
            <div className="my-groups-header">
              <h2 className="section-title">My Groups</h2>
              <button className="create-group-btn" onClick={handleCreateGroup}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <line x1="12" y1="5" x2="12" y2="19" stroke="#9747FF" strokeWidth="2" />
                  <line x1="5" y1="12" x2="19" y2="12" stroke="#9747FF" strokeWidth="2" />
                </svg>
                <span>Create a new group</span>
              </button>
            </div>

            {/* Warning for unassigned students */}
            {unassignedStudents.length > 0 && customGroups.length > 0 && (
              <div className="unassigned-warning">
                <div className="warning-content">
                  <div className="warning-header">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
                        stroke="#F8A354"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        fill="none"
                      />
                      <line x1="12" y1="9" x2="12" y2="13" stroke="#F8A354" strokeWidth="2" strokeLinecap="round" />
                      <line x1="12" y1="17" x2="12.01" y2="17" stroke="#F8A354" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    <p className="warning-text">
                      Some students haven't been assigned a group. Please add them to a group so they don't get missed.
                    </p>
                  </div>
                  <div className="unassigned-students-list">
                    {unassignedStudents.map((student, index) => (
                      <span key={student.id || index} className="unassigned-student-name">
                        {student.name}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Custom Groups List */}
            <div className="custom-groups-list">
              {customGroups.map((group) => (
                <div key={group.id} className="group-item custom-group">
                  <div className="group-row">
                    <div className="group-left">
                      <div className="group-info">
                        <h3 className="group-name">{group.name}</h3>
                        <button
                          className="student-list-toggle"
                          onClick={() => toggleGroup(group.id)}
                        >
                        <span>{group.students.length} students</span>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path
                            d="M4 6L8 10L12 6"
                            stroke="#9747FF"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            style={{
                              transform: expandedGroups.includes(group.id)
                                ? "rotate(180deg)"
                                : "rotate(0deg)",
                              transformOrigin: "center",
                              transition: "transform 0.3s ease",
                            }}
                          />
                        </svg>
                      </button>
                      </div>
                    </div>

                    <div className="group-actions">
                      <button
                        className="edit-group-btn"
                        onClick={() => handleEditGroup(group.id)}
                      >
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                          <path
                            d="M2.25 15.75H4.5L14.87 5.38C15.26 4.99 15.48 4.47 15.48 3.92C15.48 3.37 15.26 2.85 14.87 2.46C14.48 2.07 13.96 1.85 13.41 1.85C12.86 1.85 12.34 2.07 11.95 2.46L1.58 12.83V15.08"
                            stroke="#333333"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <line x1="10.5" y1="4.5" x2="13.5" y2="7.5" stroke="#333333" strokeWidth="2" />
                        </svg>
                        <span>Edit group</span>
                      </button>
                    </div>
                  </div>
                  
                  {expandedGroups.includes(group.id) && (
                    <div className="student-list-bottom">
                      {group.students.length > 0 ? (
                        <div className="student-columns">
                          {Array.from({ length: Math.ceil(group.students.length / 5) }, (_, columnIndex) => (
                            <div key={columnIndex} className="student-column">
                              {group.students
                                .slice(columnIndex * 5, (columnIndex + 1) * 5)
                                .map((student, index) => (
                                  <span key={student.id || index} className="student-name">
                                    {student.name}
                                  </span>
                                ))
                              }
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="no-students">No students in this group</span>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {customGroups.length === 0 && (
                <div className="no-custom-groups">
                  <p>No custom groups created yet. Click "Create a new group" to get started.</p>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      <EditGroupModal
        isOpen={editingGroup !== null}
        onClose={() => setEditingGroup(null)}
        groupName={editingGroup?.name || ""}
        selectedStudents={editingGroup?.students?.map(s => s.name) || []}
        availableStudents={allStudents}
        studentsInOtherCustomGroups={GroupUtils.getStudentsInOtherCustomGroups(
          customGroups.map(g => ({ ...g, isDefault: false })),
          editingGroup?.id
        )}
        onSave={handleSaveGroup}
        isLoading={isUpdatingGroup}
      />

      <CreateGroupModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        availableStudents={defaultGroupStudents}
        studentsInCustomGroups={studentsInCustomGroups}
        existingGroupNames={existingGroupNames}
        onCreate={handleCreateNewGroup}
        isLoading={isCreatingGroup}
      />
    </div>
  );
};

export default GroupView;
